import { Image } from "~/components/reusable/Image"
import { useEffect, useMemo, useState } from "react"
import { capitalize, maxBy } from "lodash"
import { groupBy } from "~/utils/tsUtils"

import { type Organization } from "~/server/api/routers/auth0"
import { api } from "~/utils/api"
import {
  DateFormatType,
  formatRelativeDate,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
  startOfTodayUTC,
} from "~/utils/datetime"

import { HakioIcon } from "../icons"
import { Alert } from "../reusable/Alert"
import HorizontalArrow from "../reusable/HorizontalArrow"
import { Skeleton } from "../reusable/Skeleton"
import { Check, DownloadSimple, Info, Warning, WarningCircle } from "@phosphor-icons/react"
import { getTailwindValue } from "~/utils/tailwind"
import { useAuth0 } from "@auth0/auth0-react"
import { useUser } from "~/providers/userProvider"

interface OrgIngestionOverviewProps {
  orgId: string
  simple?: boolean
}

const statusArrow = (color: "green" | "orange" | "red" | "violet", loading: boolean = false) => {
  let bgColor = ""
  let textColor = ""

  switch (color) {
    case "green":
      bgColor = "bg-green-600"
      textColor = "text-green-600"
      break
    case "red":
      bgColor = "bg-red-500"
      textColor = "text-red-500"
      break
    case "orange":
      bgColor = "bg-orange-500"
      textColor = "text-orange-500"
      break
    case "violet":
      bgColor = "bg-violet-500"
      textColor = "text-violet-500"
      break
  }

  return (
    <div className="flex flex-row justify-between w-full items-center h-14">
      <div className={`h-1 w-full ${bgColor}`} />
      {loading && (
        <DownloadSimple className="aspect-square h-10 min-w-10 text-violet-500 animate-bounce" />
      )}
      {color === "green" && !loading && <Check className={`h-14 min-w-14 ${textColor}`} />}
      {color === "orange" && !loading && <Warning className={`h-14 min-w-14 ${textColor}`} />}
      {color === "red" && !loading && <WarningCircle className={`h-14 min-w-14 ${textColor}`} />}
      {color === "violet" && !loading && <Info className={`h-14 min-w-14 ${textColor}`} />}

      <HorizontalArrow style={color} />
    </div>
  )
}

export const OrgIngestionOverview: React.FC<OrgIngestionOverviewProps> = ({
  orgId,
  simple = false,
}) => {
  const { isAuthenticated } = useAuth0()
  const { permissions } = useUser()
  const { data: organizations } = api.auth0.getUserOrganizations.useQuery()

  const [currentlyIngestingData, setCurrentlyIngestingData] = useState(false)
  const { data: validationTests } = api.bigQuery.validationTestResults.useQuery()

  const failedTests =
    validationTests?.filter((test) => !test.success).filter((test) => test.orgId === orgId) ?? []

  const { data: updateMeta } = api.updatedAt.getOrgTableMetaLogs.useQuery(
    { orgId },
    { refetchInterval: currentlyIngestingData ? 5000 : 30000 },
  )
  const latestUpdated = maxBy(updateMeta, (meta) => meta.endTime)?.endTime
  const { data: updatedAt } = api.updatedAt.get.useQuery(undefined, {
    refetchInterval: currentlyIngestingData ? 5000 : 30000,
  })

  const { data: latestValidationReport } = api.bigQuery.latestValidationReport.useQuery({ orgId })
  const { data: latestValidationReportDate } = api.bigQuery.latestValidationReportDate.useQuery({
    orgId,
  })
  const organization = organizations?.find((org: Organization) => org.name === orgId)
  const validationGroupedByResult = groupBy(latestValidationReport, "testResult")

  useEffect(() => {
    if (updatedAt && updatedAt.startTime) {
      setCurrentlyIngestingData(
        !updatedAt.endTime ||
          (isToday(updatedAt.startTime) && isAfter(updatedAt.startTime, updatedAt.endTime)),
      )
    } else {
      return setCurrentlyIngestingData(false)
    }
  }, [updatedAt])

  const validationStatus = useMemo(() => {
    return {
      success: validationGroupedByResult["ValidationResult.SUCCESS"] ?? [],
      info: validationGroupedByResult["ValidationResult.INFO"] ?? [],
      warning: validationGroupedByResult["ValidationResult.WARNING"] ?? [],
      failure: validationGroupedByResult["ValidationResult.FAILURE"] ?? [],
    }
  }, [validationGroupedByResult])

  const ingestionDateAfterValidationDate = useMemo(() => {
    if (latestValidationReportDate && latestUpdated) {
      return isBefore(latestValidationReportDate, latestUpdated)
    }
    return false
  }, [latestValidationReportDate, latestUpdated])

  const validationMessage = useMemo(() => {
    return `
      ${validationStatus.success.length > 0 ? "Success: " + validationStatus.success.length : ""}
      ${validationStatus.info.length > 0 ? " Info: " + validationStatus.info.length : ""}
      ${validationStatus.warning.length > 0 ? " Warning: " + validationStatus.warning.length : ""}
      ${validationStatus.failure.length > 0 ? " Failure: " + validationStatus.failure.length : ""}
      ${failedTests.length > 0 ? " View fails: " + failedTests.length : ""}
      `
  }, [validationStatus])

  const validationStatusColor = useMemo(() => {
    if (validationStatus.failure?.length > 0) {
      return "red"
    } else if (validationStatus.warning?.length > 0) {
      return "orange"
    } else if (validationStatus.info.length > 0) {
      return "violet"
    } else {
      return "green"
    }
  }, [validationStatus])

  const validationAlerts = useMemo(() => {
    let variant: "default" | "destructive" | "warning" | "info" = "default"
    switch (validationStatusColor) {
      case "red":
        variant = "destructive"
        break
      case "orange":
        variant = "warning"
        break
      case "violet":
        variant = "info"
        break
      case "green":
        variant = "default"
        break
    }

    return (
      <Alert variant={variant} className="flex flex-col gap-2">
        {validationStatus.failure.length > 0 && <b className="text-base">Failure</b>}
        {validationStatus.failure.map((report) => (
          <>
            <p key={report.testTitle}>
              <b>{report.testTitle}</b>: {report.testDescription}
            </p>
          </>
        ))}
        {failedTests.length > 0 && <b className="text-base">Failed view tests</b>}
        {failedTests.map((test) => (
          <>
            <p key={test.viewName + test.testName}>
              {test.viewName}: {test.testName}
            </p>
          </>
        ))}
        {validationStatus.warning.length > 0 && <b className="text-base">Warning</b>}
        {validationStatus.warning.map((report) => (
          <>
            <p key={report.testTitle}>
              <b>{report.testTitle}</b>: {report.testDescription}
            </p>
          </>
        ))}
        {validationStatus.info.length > 0 && <b className="text-base">Info</b>}
        {validationStatus.info.map((report) => (
          <>
            <p key={report.testTitle}>
              <b>{report.testTitle}</b>: {report.testDescription}
            </p>
          </>
        ))}
      </Alert>
    )
  }, [validationStatus, validationStatusColor])

  const validationStatusArrow = useMemo(() => {
    return statusArrow(validationStatusColor)
  }, [validationStatusColor])

  const ingestionStatus = useMemo(() => {
    return {
      success:
        (latestUpdated &&
          updateMeta?.filter((u) => u.endTime && isSameDay(u.endTime, latestUpdated))) ??
        [],
      failure:
        (latestUpdated &&
          updateMeta?.filter((u) => u.endTime && !isSameDay(u.endTime, latestUpdated))) ??
        [],
    }
  }, [updateMeta, latestUpdated])

  const ingestionMessage = useMemo(() => {
    if (currentlyIngestingData) {
      return "Currently ingesting data..."
    }
    if (!ingestionDateAfterValidationDate) {
      return "Data hasn't been updated since the last validation"
    }

    return latestUpdated
      ? updateMeta?.filter((u) => u.endTime && isSameDay(u.endTime, latestUpdated)).length +
          " out of " +
          updateMeta?.length +
          " tables updated at " +
          formatRelativeDate(latestUpdated)
      : "-"
  }, [updateMeta, latestUpdated, ingestionDateAfterValidationDate, currentlyIngestingData])

  const ingestionStatusArrow = useMemo(() => {
    if (currentlyIngestingData) {
      return statusArrow("violet", true)
    } else if (ingestionStatus.failure?.length > 0) {
      return statusArrow("red")
    } else if (!ingestionDateAfterValidationDate || failedTests.length) {
      return statusArrow("orange")
    } else {
      return statusArrow("green")
    }
  }, [ingestionStatus, ingestionDateAfterValidationDate, currentlyIngestingData])

  if (!updatedAt || !updateMeta || !latestValidationReportDate || !latestValidationReport) {
    return <Skeleton className="h-[144px]" />
  }

  if (!isAuthenticated) {
    return <div className="bg-subtle p-4">Not authenticated</div>
  }

  if (!permissions.isStaff) {
    return <div className="bg-subtle p-4">User not allowed!</div>
  }

  return (
    <div className="flex flex-col gap-2.5 min-w-full">
      <div className="flex flex-row rounded-l p-4 gap-2 items-center">
        {organization?.branding?.logo_url && (
          <Image
            src={organization.branding?.logo_url || "/favicon.ico"}
            alt={organization.display_name ?? "Current organization logo"}
            width={75}
            height={75}
          />
        )}
        <div className="flex flex-col justify-center gap-1 w-full items-center">
          <div className="font-medium">
            Latest validation date:{" "}
            {capitalize(
              formatRelativeDate(
                latestValidationReportDate,
                startOfTodayUTC(),
                DateFormatType.WithTime,
              ),
            )}
          </div>

          {validationStatusArrow}

          <div className="flex flex-row gap-1">{validationMessage}</div>
        </div>
        <Image
          src="https://cdn.icon-icons.com/icons2/2699/PNG/512/google_bigquery_logo_icon_168150.png"
          alt="Current organization logo"
          width={60}
          height={60}
        />

        <div className="flex flex-col justify-center gap-1 w-full items-center">
          <div className={`font-medium  ${currentlyIngestingData ? "animate-pulse" : ""}`}>
            {currentlyIngestingData ? "Ingestion started at: " : "Latest ingestion date: "}
            {latestUpdated
              ? capitalize(
                  formatRelativeDate(latestUpdated, startOfTodayUTC(), DateFormatType.WithTime),
                )
              : "-"}
          </div>

          {ingestionStatusArrow}

          <div className={`flex flex-row gap-1 ${currentlyIngestingData ? "animate-pulse" : ""}`}>
            {ingestionMessage}
          </div>
        </div>

        <div className="bg-hakio-green-dark h-14 min-w-14 rounded-full flex items-center justify-center shadow-sm">
          <HakioIcon width="25" height="25" color={getTailwindValue("--color-primary-300")} />
        </div>
      </div>
      {!simple && (
        <div className="grid grid-cols-2 gap-2.5 min-w-full">
          <div className="flex flex-col gap-2.5 h-full">{validationAlerts}</div>
          <div className="flex flex-col gap-2.5 h-full">{}</div>
        </div>
      )}
    </div>
  )
}
